/**
 * 市级中国地图（ECharts）——「我的资源 - 展业城市」只读可视化。
 *
 * - 地理数据：vendor 的 ``/geo/china-city.json``（367 面：4 直辖市 + 各省地级市/自治州/地区）。
 * - 已配置城市（``configured`` 键）→ 橙色底（LEO ``#FF6B35``）；
 *   悬停已配置城市 → 蓝色高亮（``#3b82f6``）+ tooltip 展示该城市地域id 列表。
 * - 悬停未配置城市 → 浅灰 + tooltip「非展业城市」。
 * - 只读模式（不传 ``onPickCity``）：不绑定 click、tooltip 不出现「点击添加」；
 *   传入 ``onPickCity`` 则保留编辑语义（点击城市回调，``configured`` 表示该城市是否已配置）。
 * - geo 加载后 / configured 变化时，经 ``onUnmatched`` 回报 configured 中
 *   无市级面匹配的键（省级/其他），由页面在地图下方以列表方式展示。
 *
 * tree-shakeable 导入：core + MapChart + GeoComponent + TooltipComponent + CanvasRenderer。
 *   （CanvasRenderer 必须显式注册，否则 ``echarts.init`` 抛 "Renderer not imported" → 地图空白。）
 */
import { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { MapChart } from "echarts/charts";
import { TooltipComponent, GeoComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { ECharts } from "echarts/core";

echarts.use([MapChart, GeoComponent, TooltipComponent, CanvasRenderer]);

// LEO 设计 token（见 docs/frontend_ui_guidline/leo-frontend-design.md）。
const COLOR_CONFIGURED = "#ff6b35"; // 已配置橙
const COLOR_CONFIGURED_BORDER = "#ea580c";
const COLOR_HOVER_CONFIGURED = "#3b82f6"; // 悬停已配置蓝高亮
const COLOR_HOVER_CONFIGURED_BORDER = "#60a5fa";
const COLOR_DEFAULT = "#DCDCDC"; // 非展业城市背景 Gainsboro（RGB 220 220 220，使橙色展业城市更突出）
const COLOR_DEFAULT_BORDER = "#5b6573";
const COLOR_HOVER_DEFAULT = "#5a6473"; // 悬停非展业城市略亮

interface ChinaCityMapProps {
  /** 已配置城市：键=城市名、值=地域id 列表。 */
  configured: Record<string, number[]>;
  /** 点击城市回调（编辑模式）；缺省时地图为只读（不绑定 click）。 */
  onPickCity?: (name: string, configured: boolean) => void;
  /** geo 加载后 / configured 变化时，回报 configured 中无市级面匹配的键（省级/其他）。 */
  onUnmatched?: (names: string[]) => void;
}

export function ChinaCityMap({ configured, onPickCity, onUnmatched }: ChinaCityMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);
  // formatter / click 闭包读取最新 configured / onPickCity，避免 stale closure。
  const configuredRef = useRef(configured);
  configuredRef.current = configured;
  const onPickCityRef = useRef(onPickCity);
  onPickCityRef.current = onPickCity;
  const geoNamesRef = useRef<Set<string> | null>(null);
  const onUnmatchedRef = useRef(onUnmatched);
  onUnmatchedRef.current = onUnmatched;

  function reportUnmatched(cfg: Record<string, number[]>) {
    if (!onUnmatchedRef.current || !geoNamesRef.current) return;
    const names = geoNamesRef.current;
    const unmatched = Object.keys(cfg).filter((n) => !names.has(n));
    onUnmatchedRef.current(unmatched);
  }

  function buildData(cfg: Record<string, number[]>) {
    return Object.entries(cfg).map(([name, ids]) => ({
      name,
      value: ids,
      itemStyle: {
        areaColor: COLOR_CONFIGURED,
        borderColor: COLOR_CONFIGURED_BORDER,
      },
      emphasis: {
        itemStyle: {
          areaColor: COLOR_HOVER_CONFIGURED,
          borderColor: COLOR_HOVER_CONFIGURED_BORDER,
        },
      },
    }));
  }

  function buildOption(cfg: Record<string, number[]>) {
    return {
      tooltip: {
        trigger: "item" as const,
        formatter: (p: { name: string }) => {
          const ids = configuredRef.current[p.name];
          if (ids && ids.length > 0) {
            return `<b>${p.name}</b><br/>地域id：[${ids.join(", ")}]`;
          }
          if (ids) return `<b>${p.name}</b><br/>地域id：[]`;
          // 只读模式（未传 onPickCity）不提示「点击添加」。
          if (!onPickCityRef.current) {
            return `${p.name}<br/><span style="color:#94a3b8">非展业城市</span>`;
          }
          return `${p.name}<br/><span style="color:#94a3b8">点击添加</span>`;
        },
      },
      series: [
        {
          type: "map" as const,
          map: "china-city",
          roam: true,
          scaleLimit: { min: 1, max: 8 },
          label: { show: false },
          itemStyle: {
            areaColor: COLOR_DEFAULT,
            borderColor: COLOR_DEFAULT_BORDER,
          },
          emphasis: {
            itemStyle: { areaColor: COLOR_HOVER_DEFAULT },
            label: { show: false },
          },
          select: { disabled: true },
          data: buildData(cfg),
        },
      ],
    };
  }

  // 挂载：fetch geo → registerMap → init → setOption → click（仅编辑模式）。
  useEffect(() => {
    let disposed = false;
    (async () => {
      let geo: unknown;
      try {
        const res = await fetch("/geo/china-city.json");
        geo = await res.json();
      } catch {
        return;
      }
      if (disposed || !containerRef.current) return;
      const geoObj = geo as { features?: { properties?: { name?: string } }[] };
      geoNamesRef.current = new Set(
        (geoObj.features ?? [])
          .map((f) => f?.properties?.name)
          .filter((n): n is string => Boolean(n)),
      );
      echarts.registerMap("china-city", geo as Parameters<typeof echarts.registerMap>[1]);
      let chart: ECharts;
      try {
        chart = echarts.init(containerRef.current);
        chart.setOption(buildOption(configuredRef.current));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[ChinaCityMap] echarts init/setOption failed:", err);
        return;
      }
      chartRef.current = chart;
      reportUnmatched(configuredRef.current);
      chart.on("click", (p: { name: string }) => {
        if (!onPickCityRef.current) return;
        onPickCityRef.current(
          p.name,
          Object.prototype.hasOwnProperty.call(configuredRef.current, p.name),
        );
      });
    })();
    const onResize = () => chartRef.current?.resize();
    window.addEventListener("resize", onResize);
    return () => {
      disposed = true;
      window.removeEventListener("resize", onResize);
      chartRef.current?.dispose();
      chartRef.current = null;
    };
    // 仅挂载时跑一次；configured 变化由下方 effect 处理。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // configured 变化：增量更新 data + 重新分类 unmatched（不重建图表）。
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.setOption(buildOption(configured));
    }
    reportUnmatched(configured);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configured]);

  return <div ref={containerRef} className="h-[560px] w-full" />;
}
